import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useRouteContext, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { Button, EmptyState, ErrorState } from '@bb/ui';
import { ApiError, type CalendarFilters } from '../../api/client.ts';
import { CHANNEL_LABELS } from '../campaigns/labels.ts';
import { addDays, addMonths, calendarSearch, monthLabel, rangeLabel, rangeOf, todayLocal, type CalendarSearch, type CalendarViewMode } from './dates.ts';
import { MonthView } from './MonthView.tsx';
import { WeekView } from './WeekView.tsx';

const from = '/env/$environmentId/calendar' as const;
const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');

/**
 * Messaging → Content Calendar (BB-108): planned sends of live campaigns and Canvases by week or month, with send
 * windows of action-based and API-triggered delivery and the days where sends overlap on a channel.
 */
export function CalendarPage() {
  const { environmentId } = useParams({ from });
  const { api } = useRouteContext({ from });
  const search = useSearch({ from });
  const navigate = useNavigate({ from });
  const today = todayLocal();
  const view: CalendarViewMode = search.view ?? 'week';
  const date = search.date ?? today;
  const range = rangeOf(view, date);
  const go = (patch: CalendarSearch) => navigate({ search: (prev: CalendarSearch) => calendarSearch({ ...prev, ...patch }), replace: true, resetScroll: false });
  const [objectType, setObjectType] = useState<CalendarFilters['objectType'] | ''>('');
  const [channel, setChannel] = useState<CalendarFilters['channel'] | ''>('');
  const [status, setStatus] = useState<CalendarFilters['status'] | ''>('');
  const [tagId, setTagId] = useState('');
  const filters: CalendarFilters = { ...range, ...(objectType ? { objectType } : {}), ...(channel ? { channel } : {}), ...(status ? { status } : {}), ...(tagId ? { tagId } : {}) };
  const calendar = useQuery({ queryKey: ['calendar', environmentId, filters], queryFn: () => api.calendar(environmentId, filters) });
  const tags = useQuery({ queryKey: ['tags', environmentId], queryFn: () => api.listTags(environmentId) });
  const step = (n: number) => go({ date: view === 'week' ? addDays(date, 7 * n) : addMonths(date, n) });
  const data = calendar.data;
  const body = data ? { environmentId, from: data.from, to: data.to, items: data.items, overlaps: data.overlaps, today } : null;

  return (
    <section>
      <h1>Content Calendar</h1>
      <p className="hint">Planned sends of live campaigns and Canvases. Action-based and API-triggered delivery shows the period it can send in.</p>
      <div className="filters">
        <div className="calendar__nav" role="group" aria-label="Period">
          <Button variant="ghost" onClick={() => step(-1)} aria-label={view === 'week' ? 'Previous week' : 'Previous month'}>
            ‹
          </Button>
          <Button variant="ghost" onClick={() => go({ date: today })}>
            Today
          </Button>
          <Button variant="ghost" onClick={() => step(1)} aria-label={view === 'week' ? 'Next week' : 'Next month'}>
            ›
          </Button>
          <strong aria-live="polite">{view === 'week' ? rangeLabel(range.from, range.to) : monthLabel(date)}</strong>
        </div>
        <div className="calendar__nav" role="group" aria-label="View">
          {(['week', 'month'] as const).map((v) => (
            <Button key={v} variant={view === v ? 'primary' : 'ghost'} aria-pressed={view === v} onClick={() => go({ view: v })}>
              {v === 'week' ? 'Week' : 'Month'}
            </Button>
          ))}
        </div>
        <select aria-label="Filter by type" value={objectType} onChange={(e) => setObjectType(e.target.value as typeof objectType)}>
          <option value="">Campaigns and Canvases</option>
          <option value="campaign">Campaigns</option>
          <option value="canvas">Canvases</option>
        </select>
        <select aria-label="Filter by channel" value={channel} onChange={(e) => setChannel(e.target.value as typeof channel)}>
          <option value="">All channels</option>
          {Object.entries(CHANNEL_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select aria-label="Filter by status" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
          <option value="">Active and paused</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
        </select>
        <select aria-label="Filter by tag" value={tagId} onChange={(e) => setTagId(e.target.value)}>
          <option value="">All tags</option>
          {(tags.data ?? []).map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </div>
      {calendar.isPending ? (
        <p aria-busy="true">Loading calendar…</p>
      ) : calendar.isError ? (
        <ErrorState title="Calendar could not be loaded" message={errorText(calendar.error)} onRetry={() => void calendar.refetch()} />
      ) : (
        <>
          <p className="hint" role="status">
            {data!.overlaps.length ? `${data!.overlaps.length} ${data!.overlaps.length === 1 ? 'overlap' : 'overlaps'} in this ${view}: sends on the same day through the same channel.` : `No overlapping sends in this ${view}.`} Times are in {data!.timeZone}.
          </p>
          {data!.items.length === 0 ? (
            <EmptyState title="Nothing planned" description="No live campaign or Canvas sends in this period with these filters." />
          ) : view === 'week' ? (
            <WeekView {...body!} />
          ) : (
            <MonthView {...body!} month={date} onShowWeek={(day) => go({ view: 'week', date: day })} />
          )}
        </>
      )}
    </section>
  );
}
